/**
 * Card Component - Reusable card container for feature pages
 * Supports multiple variants and states
 * 
 * Usage:
 * <Card variant="default">Content</Card>
 * <Card variant="elevated">Elevated content</Card>
 * <Card variant="interactive" selected>Interactive content</Card>
 */

import React from 'react';
import { Box } from '@mui/material';
import { useFeatureTheme } from '../../../theme/hooks/useFeatureTheme';

const Card = React.forwardRef(
  (
    {
      variant = 'default',
      selected = false,
      interactive = false,
      children,
      className = '',
      sx = {},
      onClick,
      ...props
    },
    ref
  ) => {
    const { semantic, radiusPx, shadows, density, typography } = useFeatureTheme();
    const [isHovered, setIsHovered] = React.useState(false);

    const variantStyles = {
      default: {
        backgroundColor: semantic.surfaces.surface,
        border: `1px solid ${semantic.borders.border}`,
        boxShadow: shadows.xs,
      },
      elevated: {
        backgroundColor: semantic.surfaces.surfaceElevated,
        border: `1px solid ${semantic.borders.border}`,
        boxShadow: shadows.md,
      },
      interactive: {
        backgroundColor: selected ? semantic.interactive.primarySoft : semantic.surfaces.surface,
        border: `1px solid ${
          selected ? semantic.interactive.primary : semantic.borders.border
        }`,
        boxShadow: shadows.xs,
        cursor: 'pointer',
        transition: 'all 160ms ease',
      },
      muted: {
        backgroundColor: semantic.surfaces.surfaceMuted,
        border: `1px solid ${semantic.borders.border}`,
        boxShadow: 'none',
      },
      borderless: {
        backgroundColor: 'transparent',
        border: 'none',
        boxShadow: 'none',
      },
    };

    const hoverStyles =
      interactive && isHovered && !selected
        ? {
            borderColor: semantic.interactive.primary,
            boxShadow: shadows.sm,
          }
        : {};

    const focusStyles = interactive
      ? {
          '&:focus-visible': {
            outline: 'none',
            boxShadow: `0 0 0 3px ${semantic.borders.focusRing}`,
          },
        }
      : {};

    return (
      <Box
        ref={ref}
        component={interactive ? 'button' : 'div'}
        onClick={onClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        aria-pressed={interactive ? selected : undefined}
        className={className}
        sx={{
          ...variantStyles[variant],
          ...hoverStyles,
          ...focusStyles,
          borderRadius: radiusPx.lg,
          padding: `${density?.cardPadding || 16}px`,
          transition: 'all 160ms ease',
          fontFamily: typography.fontFamilyBase,
          ...(interactive && {
            textAlign: 'left',
            font: 'inherit',
            appearance: 'none',
          }),
          ...sx,
        }}
        {...props}
      >
        {children}
      </Box>
    );
  }
);

Card.displayName = 'Card';

export default Card;
